import { ChangeEvent, Fragment, RefObject } from 'react';
import { toAbsoluteUrl } from '@/utils';
import { useSettings } from '@/providers/SettingsProvider';
import { KeenIcon } from '@/components';
import { useLogoutMutation } from '@/services/AuthService';
import {
  MenuItem,
  MenuLink,
  MenuSub,
  MenuTitle,
  MenuSeparator,
  MenuIcon,
  IMenuItemRef,
} from '@/components/menu';
import { useDispatch } from 'react-redux';
import { logout } from '@/store/authSlice';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserData } from '@/types/auth.types';

interface IDropdownUserProps {
  menuItemRef: RefObject<IMenuItemRef>;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DropdownUser = ({ menuItemRef }: IDropdownUserProps) => {
  const { settings, storeSettings } = useSettings();
  const [logoutMutation] = useLogoutMutation();
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.auth.userData);
  const roles = userData?.roles || [];
  const canSeeKyc =
    roles.includes('marina_manager') ||
    roles.includes('yacht_club_admin') ||
    roles.includes('service_provider');

  const profile_pic = useSelector(
    (state: RootState) => state.auth.profile_picture
  );

  const handleThemeMode = (event: ChangeEvent<HTMLSelectElement>) => {
    const newThemeMode = event.target.value as 'light' | 'dark' | 'contrast';

    storeSettings({
      themeMode: newThemeMode,
    });
  };

  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap();
      // Only clear token and navigate if the API call was successful
      dispatch(logout());
    } catch (error) {
      console.error('Logout failed:', error);
      // You might want to show an error message to the user here
    }
  };

  const buildHeader = (userData: UserData | null) => {
    return (
      <header className="flex items-center justify-between px-5 py-1.5 gap-1.5">
        <div className="flex items-center gap-2">
          <img
            className="size-9 rounded-full border-2 border-success"
            src={
              (profile_pic as string) ||
              toAbsoluteUrl('/media/avatars/defult_avtar.png')
            }
            alt="avatar"
          />
          <div className="flex flex-col gap-1.5">
            <div className="text-sm text-gray-800 font-semibold leading-none">
              {userData?.name || 'Admin'}
            </div>
            <div className="text-xs text-gray-600 font-medium leading-none truncate max-w-[134px]">
              {userData?.email || 'No email available'}
            </div>
          </div>
        </div>
      </header>
    );
  };

  const buildMenu = () => {
    return (
      <Fragment>
        <MenuSeparator />
        <div className="flex flex-col">
          <MenuItem>
            <MenuLink path="/account/home/user-profile">
              <MenuIcon>
                <KeenIcon icon="profile-circle" />
              </MenuIcon>
              <MenuTitle>My Profile</MenuTitle>
            </MenuLink>
          </MenuItem>
          <MenuItem>
            <MenuLink path="/change-password">
              <MenuIcon>
                <KeenIcon icon="key-square" />
              </MenuIcon>
              <MenuTitle>Change Password</MenuTitle>
            </MenuLink>
          </MenuItem>
          {canSeeKyc && (
            <MenuItem>
              <MenuLink path="/user-kyc">
                <MenuIcon>
                  <KeenIcon icon="shield-tick" />
                </MenuIcon>
                <MenuTitle>KYC</MenuTitle>
              </MenuLink>
            </MenuItem>
          )}
          {/* <DropdownUserLanguages menuItemRef={menuItemRef} /> */}
          <MenuSeparator />
        </div>
      </Fragment>
    );
  };

  const buildFooter = () => {
    return (
      <footer className="flex flex-col">
        <div className="menu-item mb-0.5">
          <div className="menu-link flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="menu-icon">
                <KeenIcon icon="moon" />
              </span>
              <label htmlFor="theme-select" className="menu-title">
                Theme
              </label>
            </div>
            <select
              id="theme-select"
              name="theme"
              value={settings.themeMode}
              onChange={handleThemeMode}
              className="select select-sm h-7 text-xs border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary  shrink-0"
              style={{
                width: '90px',
                maxWidth: '90px',
                fontSize: '0.75rem',
                paddingLeft: '0.375rem',
                paddingRight: '0.375rem',
              }}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="contrast">Contrast</option>
            </select>
          </div>
        </div>

        <div className="menu-item px-4 py-1.5">
          <button
            type="button"
            onClick={() => handleLogout()}
            className="btn btn-sm btn-light justify-center w-full"
          >
            Logout
          </button>
        </div>
      </footer>
    );
  };

  return (
    <MenuSub
      className="menu-default light:border-gray-300 w-[200px] md:w-[250px]"
      rootClassName="p-0"
    >
      <nav className="flex flex-col" aria-label="User information">
        {buildHeader(userData)}
        {buildMenu()}
        {buildFooter()}
      </nav>
    </MenuSub>
  );
};

export { DropdownUser };
